import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Document } from '../../services/api';
import { useDocsStore } from '../../store/docsStore';
import { useConfigStore } from '../../store/configStore';
import styles from './DocumentTree.module.css';

interface TreeNode {
  name: string;
  path: string;
  type: 'folder' | 'document';
  children?: TreeNode[];
  document?: Document;
}

interface DocumentTreeProps {
  onDocumentClick?: () => void;
}

export const DocumentTree: React.FC<DocumentTreeProps> = ({ onDocumentClick }) => {
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['']));
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  
  const { documents, isLoading, fetchDocuments } = useDocsStore();
  const { config } = useConfigStore();

  // Find current document and compute folders to expand
  const currentDocument = useMemo(() => {
    if (!slug || !documents) return null;
    return documents.find(doc => doc.slug === slug) || null;
  }, [slug, documents]);

  // Auto-expand folders containing current document
  useEffect(() => {
    if (currentDocument) {
      const pathParts = currentDocument.folder_path.split('/').filter(Boolean);
      const foldersToExpand = pathParts.map((_, index) => 
        pathParts.slice(0, index + 1).join('/')
      );
      setExpandedFolders(prev => {
        const newSet = new Set(prev);
        newSet.add(''); // root always expanded
        foldersToExpand.forEach(folder => newSet.add(folder));
        return newSet;
      });
    }
  }, [currentDocument]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (documents) {
      const treeStructure = buildTree(documents);
      setTree(treeStructure);
    }
  }, [documents, config.navigationOrder]);

  const buildTree = (documents: Document[]): TreeNode[] => {
    const root: TreeNode = { name: 'root', path: '', type: 'folder', children: [] };

    documents.forEach((doc) => {
      const pathParts = doc.folder_path.split('/').filter(Boolean);
      let currentNode = root;

      pathParts.forEach((part, index) => {
        const currentPath = pathParts.slice(0, index + 1).join('/');
        let childNode = currentNode.children?.find(
          (child) => child.name === part && child.type === 'folder'
        );

        if (!childNode) {
          childNode = {
            name: part,
            path: currentPath,
            type: 'folder',
            children: []
          };
          currentNode.children = currentNode.children || [];
          currentNode.children.push(childNode);
        }

        currentNode = childNode;
      });

      // Add document as leaf
      const docNode: TreeNode = {
        name: doc.title,
        path: doc.folder_path,
        type: 'document',
        document: doc
      };
      currentNode.children = currentNode.children || [];
      currentNode.children.push(docNode);
    });

    // Sort children: folders first or documents first based on config
    const sortChildren = (node: TreeNode) => {
      if (node.children) {
        node.children.sort((a, b) => {
          if (a.type !== b.type) {
            // If navigation order is 'documents_first', reverse the logic
            if (config.navigationOrder === 'documents_first') {
              return a.type === 'document' ? -1 : 1;
            }
            // Default: folders first
            return a.type === 'folder' ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        });
        node.children.forEach(sortChildren);
      }
    };

    sortChildren(root);
    return root.children || [];
  };

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleDocumentClick = (doc: Document) => {
    navigate(`/docs/${doc.slug}`);
    onDocumentClick?.();
  };

  const renderTree = (nodes: TreeNode[], level: number = 0): React.ReactNode => {
    return nodes.map((node) => {
      if (node.type === 'folder') {
        const isExpanded = expandedFolders.has(node.path);
        return (
          <div key={node.path} className={styles.folderContainer}>
            <div
              className={styles.folderItem}
              style={{ paddingLeft: `${level * 16 + 8}px` }}
              onClick={() => toggleFolder(node.path)}
            >
              <span className={styles.folderIcon}>{isExpanded ? '▼' : '▶'}</span>
              <span className={styles.folderName}>{node.name}</span>
            </div>
            {isExpanded && node.children && (
              <div className={styles.folderChildren}>
                {renderTree(node.children, level + 1)}
              </div>
            )}
          </div>
        );
      } else {
        const isSelected = node.document?.slug === slug;
        return (
          <div
            key={node.document?.id}
            className={`${styles.documentItem} ${isSelected ? styles.documentItemSelected : ''}`}
            style={{ paddingLeft: `${level * 16 + 24}px` }}
            onClick={() => node.document && handleDocumentClick(node.document)}
          >
            <span className={styles.documentIcon}>📄</span>
            <span className={styles.documentName}>{node.name}</span>
          </div>
        );
      }
    });
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading documents...</div>;
  }

  return (
    <div className={styles.treeContainer}>
      <div className={styles.treeHeader}>Documents</div>
      <div className={styles.tree}>{renderTree(tree)}</div>
    </div>
  );
};
